import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AiOutlinePrinter,
  AiOutlineDownload,
  AiOutlineCopy,
} from "react-icons/ai";
import { MdWarningAmber } from "react-icons/md";
import styled from "styled-components";
import axios from "axios";
import openNotification from "../utils/CustomNotification";
import "./RecoveryCodeDisplay.css";
import logo1 from "../assets/logo1.png";

const beAddress = process.env.REACT_APP_API_URL;

const RecoveryCodeDisplay: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const showCount = sessionStorage.getItem("showRecovery") === "true";

  if (!location.state || (showCount !== true && showCount !== false)) {
    // either redirect:
    <div>Access denied or invalid access. Please navigate properly.</div>;
    // or just hide page:
    // return null;
  }

  const [codes, setCodes] = useState<string[]>([]);
  const [isRegenerated, setIsRegenerated] = useState(false);
  const [usedCodesCount, setUsedCodesCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const codesRef = useRef<HTMLUListElement>(null);
  const hasRegenerated = useRef(false);

  const userUid = localStorage.getItem("user_uid");
  const sessionUid = localStorage.getItem("session_uid");

  const fetchUsedCodesCount = async () => {
    try {
      const response = await axios.get(
        `${beAddress}/api/recovery-backup-code-count`,
        {
          headers: {
            "session-uid": sessionUid,
            "user-uid": userUid,
          },
        },
      );

      if (response?.data?.status === "Success") {
        const value = response.data.data;
        setUsedCodesCount(
          Array.isArray(value) && value.length === 0 ? 0 : value,
        );
      }
    } catch {
      openNotification({
        type: "error",
        message: "Failed to fetch recovery code count.",
      });
    }
  };

  const acknowledgeBackupCodes = async () => {
    try {
      await axios.post(
        `${beAddress}/api/setup-2fa-recovery-backup-code`,
        true,
        {
          headers: {
            "user-uid": userUid,
          },
          withCredentials: true,
        },
      );
    } catch (error) {
      console.error("Failed to acknowledge recovery codes:", error);
    }
  };

  const handleAction = async (actionFn: () => void) => {
    await acknowledgeBackupCodes();
    sessionStorage.setItem("showRecovery", "true");
    actionFn();
  };

  const handleDownload = () => {
    const blob = new Blob([codes.join("\n")], { type: "text/plain" });
    const link = document.createElement("a");
    link.download = "cofit-recovery-codes.txt";
    link.href = URL.createObjectURL(blob);
    link.click();
  };

  const handlePrint = (): void => {
    const printWindow = window.open("", "Cofit");

    if (!printWindow) {
      console.error("Failed to open print window.");
      return;
    }

    const logoUrl = logo1;
    const siteUrl = window.location.origin;

    const codesList = codes.map((code: string) => `<li>${code}</li>`).join("");

    const htmlContent = `
  <html>
    <head>
      <title>Cofit</title>
      <style>
        @page {
          margin: 20mm;
        }
        body {
          font-family: Arial, sans-serif;
          text-align: center;
          padding: 20px;
          color: #000;
          margin: 0;
        }
        .container {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
          border: 1px solid #ddd;
          border-radius: 10px;
          box-shadow: 0 0 6px rgba(0,0,0,0.1);
        }
        img.print-logo {
          max-width: 120px;
          margin-bottom: 20px;
          display: block;
          margin-left: auto;
          margin-right: auto;
        }
        h2 {
          margin: 10px 0 20px;
          font-size: 22px;
          font-weight: bold;
        }
        ul.codes-list {
          padding: 20px;
          margin: 20px auto;
          display: inline-block;
          text-align: left;
          font-size: 16px;
          letter-spacing: 1px;
          border-radius: 8px;
          list-style-type: disc;
          max-width: 350px;
          border: 2px solid #000;
          background: #f9f9f9;
        }
        .footer {
          margin-top: 25px;
          font-size: 13px;
          color: #555;
        }
        footer.site-url {
          position: fixed;
          bottom: 10px;
          left: 10px;
          font-size: 12px;
          color: #333;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <img src="${logoUrl}" class="print-logo" alt="Cofit Logo" />
        <h2>Recovery Codes</h2>
        <ul class="codes-list">
          ${codesList}
        </ul>
        <div class="footer">Cofit Two-Factor Authentication Account Recovery Codes</div>
      </div>
      <footer class="site-url">${siteUrl}</footer>
      <script>
        window.onload = function() {
          window.print();
          window.onafterprint = window.close;
        }
      </script>
    </body>
  </html>
  `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      openNotification({
        type: "success",
        message: "Recovery codes copied to clipboard!",
      });
    } catch {
      openNotification({
        type: "error",
        message: "Failed to copy recovery codes!",
      });
    }
  };

  useEffect(() => {
    if (showCount) {
      fetchUsedCodesCount();
    }
  }, [showCount]);

  useEffect(() => {
    if (!showCount && usedCodesCount === 0 && !hasRegenerated.current) {
      handleRegenerate();
      hasRegenerated.current = true;
    }
  }, [showCount, usedCodesCount]);

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${beAddress}/api/generate-2fa-recovery-backup-code`,
        {
          headers: {
            "user-uid": userUid,
          },
          withCredentials: true,
        },
      );

      if (
        response?.data?.status === "Success" &&
        Array.isArray(response.data.data)
      ) {
        setCodes(response.data.data);
        setIsRegenerated(true);
        openNotification({
          type: "success",
          message: "New recovery codes generated successfully.",
        });
      } else {
        throw new Error("Invalid response from server");
      }
    } catch {
      openNotification({
        type: "error",
        message: "Failed to generate new recovery codes.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="recovery-codes">
      <h4 className="fade-in">Recovery Codes</h4>

      {codes.length === 0 ? (
        <>
          {showCount ? (
            <>
              <p className="recovery-info-display-used fade-in">
                You currently have <strong>{usedCodesCount}</strong> recovery
                codes left.
              </p>
              <ActionButton onClick={handleRegenerate} disabled={loading}>
                {loading ? "Generating..." : "Generate New Recovery Codes"}
              </ActionButton>
            </>
          ) : (
            <>
              <p className="recovery-note fade-in"></p>
              <ActionButton onClick={handleRegenerate} disabled={loading}>
                {loading ? "Generating..." : "Generate New Recovery Codes"}
              </ActionButton>
            </>
          )}
        </>
      ) : (
        <>
          <p className="recovery-info-display fade-in">
            <MdWarningAmber className="recovery-info-icon" />
            If you lose access to your authentication device, you can use one of
            these backup codes to login to your account. Each code may be used
            only once. Make a copy of these codes, and store it somewhere safe.
          </p>

          <CodeList ref={codesRef} className="fade-in">
            {codes.map((code, index) => (
              <li key={index}>{code}</li>
            ))}
          </CodeList>

          <Note>
            To activate your recovery codes, you must either Download, Copy or
            Print them.
          </Note>

          <div className="recovery-buttons fade-in">
            <button onClick={() => handleAction(handlePrint)}>
              <AiOutlinePrinter /> Print
            </button>
            <button onClick={() => handleAction(handleDownload)}>
              <AiOutlineDownload /> Download
            </button>
            <button onClick={() => handleAction(handleCopy)}>
              <AiOutlineCopy /> Copy
            </button>
          </div>

          <div className="regenerate-note-box fade-in">
            {/* <h5>Generate new recovery codes</h5> */}
            <p className="regenerate-note">
              When you generate new recovery codes, you must download or print
              the new codes. <b>Your old codes won't work anymore</b>
            </p>
          </div>

          <ButtonGroup className="fade-in">
            <button onClick={handleRegenerate} disabled={loading}>
              {loading ? "Generating..." : "Generate New Recovery Codes"}
            </button>
          </ButtonGroup>
        </>
      )}
    </div>
  );
};

export default RecoveryCodeDisplay;

// Styled components
const Note = styled.p`
  font-size: 0.875rem;
  margin-top: 1.5rem;
`;

const CodeList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 0.6rem;

  li {
    flex: 0 0 45%;
    padding: 0.4rem 0.6rem;
    font-size: 0.75rem;
    border: 1px dotted #888;
    border-radius: 6px;
    background-color: #f9f9f9;
    color: #333;
    text-align: center;
    word-break: break-word;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 1.5rem;

  button {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #094752;
    border: none;
    color: white;
    padding: 0.6rem 1.2rem;
    font-size: 0.95rem;
    border-radius: 8px;
    cursor: pointer;
    gap: 0.5rem;
    transition: background-color 0.3s;

    &:hover {
      background-color: rgb(15, 98, 112);
    }

    &:disabled {
      background-color: #ccc;
      cursor: not-allowed;
    }
  }
`;

const ActionButton = styled.button`
  background-color: #094752;
  border: none;
  color: white;
  padding: 0.6rem 1.2rem;
  font-size: 0.95rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: rgb(15, 98, 112);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;
